import { useState, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Receipt, Plus, Search, Loader2, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { InvoiceForm } from "@/components/InvoiceForm";
import { InvoiceRow } from "@/components/InvoiceRow";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Billing({ user, setUser }) {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  useEffect(function() {
    fetchInvoices();
    fetch(API_URL + "/api/clients", { credentials: "include" })
      .then(function(r) { return r.json(); })
      .then(setClients)
      .catch(function() {});
  }, []);

  async function fetchInvoices() {
    try {
      var url = API_URL + "/api/invoices" + (filterStatus ? "?status=" + filterStatus : "");
      var res = await fetch(url, { credentials: "include" });
      if (res.ok) setInvoices(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }

  useEffect(function() { fetchInvoices(); }, [filterStatus]);

  async function handleDelete(invoiceId) {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      var res = await fetch(API_URL + "/api/invoices/" + invoiceId, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Facture supprimée"); fetchInvoices(); }
    } catch (e) { toast.error("Erreur"); }
  }

  async function handleDownloadPdf(invoiceId) {
    try {
      var res = await fetch(API_URL + "/api/invoices/" + invoiceId + "/pdf", { credentials: "include" });
      if (res.ok) {
        var blob = await res.blob();
        var url = URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url; a.download = "facture.pdf"; a.click();
        URL.revokeObjectURL(url);
      } else { toast.error("Erreur PDF"); }
    } catch (e) { toast.error("Erreur"); }
  }

  async function handleStatusChange(invoiceId, newStatus) {
    try {
      var res = await fetch(API_URL + "/api/invoices/" + invoiceId, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) { toast.success("Statut mis à jour"); fetchInvoices(); }
    } catch (e) { toast.error("Erreur"); }
  }

  var filtered = invoices.filter(function(inv) {
    if (!searchQuery) return true;
    var q = searchQuery.toLowerCase();
    return inv.invoice_number.toLowerCase().includes(q) || (inv.client_name || "").toLowerCase().includes(q);
  });

  var totalRevenue = invoices.filter(function(i) { return i.status === "payee"; }).reduce(function(s, i) { return s + i.total; }, 0);
  var pendingAmount = invoices.filter(function(i) { return i.status === "envoyee" || i.status === "brouillon"; }).reduce(function(s, i) { return s + i.total; }, 0);

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="billing-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Facturation</h1>
            <p className="text-sm text-muted-foreground">{invoices.length} facture{invoices.length !== 1 ? "s" : ""}</p>
          </div>
          <Dialog open={showForm} onOpenChange={function(open) { if (!open) setEditingInvoice(null); setShowForm(open); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-invoice-btn" onClick={function() { setEditingInvoice(null); setShowForm(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Nouvelle facture
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingInvoice ? "Modifier la facture" : "Nouvelle facture"}</DialogTitle>
                <DialogDescription>Remplissez les détails de la facture</DialogDescription>
              </DialogHeader>
              <InvoiceForm invoice={editingInvoice} clients={clients} onSave={fetchInvoices} onClose={function() { setShowForm(false); setEditingInvoice(null); }} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Revenus encaissés</p>
                  <p className="text-2xl font-bold text-green-600">{totalRevenue.toFixed(2)} EUR</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">En attente</p>
                  <p className="text-2xl font-bold text-amber-600">{pendingAmount.toFixed(2)} EUR</p>
                </div>
                <Clock className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total factures</p>
                  <p className="text-2xl font-bold">{invoices.length}</p>
                </div>
                <Receipt className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} placeholder="Rechercher une facture..." className="pl-9" data-testid="search-invoices" />
          </div>
          <Select value={filterStatus || "all"} onValueChange={function(v) { setFilterStatus(v === "all" ? "" : v); }}>
            <SelectTrigger className="w-40" data-testid="filter-invoice-status">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="brouillon">Brouillon</SelectItem>
              <SelectItem value="envoyee">Envoyée</SelectItem>
              <SelectItem value="payee">Payée</SelectItem>
              <SelectItem value="annulee">Annulée</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-1">{searchQuery ? "Aucun résultat" : "Aucune facture"}</h3>
              <p className="text-sm text-muted-foreground mb-4">{searchQuery ? "Essayez une autre recherche" : "Créez votre première facture"}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filtered.map(function(inv) {
              return (
                <InvoiceRow
                  key={inv.invoice_id}
                  inv={inv}
                  onStatusChange={handleStatusChange}
                  onDownload={handleDownloadPdf}
                  onEdit={function(i) { setEditingInvoice(i); setShowForm(true); }}
                  onDelete={handleDelete}
                />
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
